import { Controller } from "@hotwired/stimulus";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

export default class extends Controller {
  static values = {
    type: String,
    labels: String,  // Cambiar a String para parsear manualmente
    data: String,     // Cambiar a String para parsear manualmente
    backgroundColor: String,
    borderColor: String,
    indexAxis: String,
    allianceNames: String  // Cambiar a String para parsear manualmente
  }

  connect() {
    if (typeof Chart === 'undefined') {
      console.error('Chart.js no está disponible');
      return;
    }

    this.initializeChart();
  }

  initializeChart() {
    const ctx = this.element;
    if (!ctx) {
      console.error('Chart controller: No canvas element found');
      return;
    }

    const chartType = this.typeValue || 'line';
    console.log('Chart controller: Initializing chart type:', chartType);
    
    // Parsear labels y data desde strings JSON
    let labels = [];
    if (this.labelsValue) {
      try {
        labels = typeof this.labelsValue === 'string' 
          ? JSON.parse(this.labelsValue) 
          : this.labelsValue;
        console.log('Chart controller: Parsed labels:', labels);
      } catch (e) {
        console.error('Error parsing labels:', e, this.labelsValue);
        labels = [];
      }
    }
    
    let data = [];
    if (this.dataValue) {
      try {
        data = typeof this.dataValue === 'string' 
          ? JSON.parse(this.dataValue) 
          : this.dataValue;
        console.log('Chart controller: Parsed data:', data);
      } catch (e) {
        console.error('Error parsing data:', e, this.dataValue);
        data = [];
      }
    }
    
    // Verificar que tenemos datos válidos
    if (!Array.isArray(labels) || !Array.isArray(data)) {
      console.error('Chart controller: Labels or data is not an array', { labels, data });
      const container = ctx.closest('.chart-container') || ctx.parentElement;
      if (container) {
        container.innerHTML = '<p class="text-muted text-center py-4">Error: Datos inválidos</p>';
      }
      return;
    }
    
    if (labels.length === 0 || data.length === 0) {
      console.warn('Chart controller: Empty data', { labelsLength: labels.length, dataLength: data.length });
      const container = ctx.closest('.chart-container') || ctx.parentElement;
      if (container) {
        container.innerHTML = '<p class="text-muted text-center py-4">No hay datos disponibles</p>';
      }
      return;
    }

    console.log('Chart controller: Creating chart with config', { labels, data, chartType });
    const config = this.getChartConfig(chartType, labels, data);
    
    try {
      const chart = new Chart(ctx, config);
      console.log('Chart controller: Chart created successfully', chart);
    } catch (error) {
      console.error('Chart controller: Error creating chart', error);
      const container = ctx.closest('.chart-container') || ctx.parentElement;
      if (container) {
        container.innerHTML = '<p class="text-danger text-center py-4">Error al crear el gráfico</p>';
      }
    }
  }

  getChartConfig(type, labels, data) {
    // Manejar backgroundColor como array o string
    let backgroundColor = this.getDefaultColor(type);
    if (this.backgroundColorValue) {
      try {
        // Si es un string JSON, parsearlo
        backgroundColor = typeof this.backgroundColorValue === 'string' 
          ? JSON.parse(this.backgroundColorValue) 
          : this.backgroundColorValue;
      } catch (e) {
        // Si no es JSON válido, usar como string simple
        backgroundColor = this.backgroundColorValue;
      }
    }
    
    // Manejar borderColor como array o string
    let borderColor = this.getDefaultBorderColor(type);
    if (this.borderColorValue) {
      borderColor = this.borderColorValue;
    }
    
    const baseConfig = {
      type: type,
      data: {
        labels: labels,
        datasets: [{
          label: this.getDatasetLabel(type),
          data: data,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: type === 'doughnut' ? 2 : 1,
          borderRadius: type === 'bar' ? 4 : 0,
          tension: type === 'line' ? 0.4 : 0,
          fill: type === 'line',
          pointRadius: type === 'line' ? 4 : 0,
          pointHoverRadius: type === 'line' ? 6 : 0,
          pointBackgroundColor: type === 'line' ? this.borderColorValue || '#0d6efd' : undefined,
          pointBorderColor: type === 'line' ? '#fff' : undefined,
          pointBorderWidth: type === 'line' ? 2 : 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: type === 'doughnut',
            position: type === 'doughnut' ? 'bottom' : 'top',
            labels: {
              padding: type === 'doughnut' ? 15 : 10,
              font: { size: 13 }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: { size: 14, weight: 'bold' },
            bodyFont: { size: 13 },
            callbacks: this.getTooltipCallbacks(type)
          }
        },
        scales: this.getScalesConfig(type)
      }
    };

    if (this.indexAxisValue === 'y') {
      baseConfig.options.indexAxis = 'y';
    }

    return baseConfig;
  }

  getDatasetLabel(type) {
    const labels = {
      'line': 'Cupones Utilizados',
      'bar': 'Cantidad',
      'doughnut': ''
    };
    return labels[type] || 'Datos';
  }

  getDefaultColor(type) {
    if (type === 'doughnut') {
      return ['#28a745', '#ffc107', '#0d6efd', '#dc3545'];
    }
    return type === 'bar' ? '#0d6efd' : 'rgba(13, 110, 253, 0.1)';
  }

  getDefaultBorderColor(type) {
    if (type === 'doughnut') {
      return '#fff';
    }
    return '#0d6efd';
  }

  getTooltipCallbacks(type) {
    if (type === 'doughnut') {
      return {
        label: (context) => {
          const label = context.label || '';
          const value = context.parsed || 0;
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(2) : 0;
          return label + ': ' + value.toLocaleString() + ' (' + percentage + '%)';
        }
      };
    }
    
    if (type === 'bar') {
      // Parsear allianceNames si es necesario
      let allianceNames = [];
      if (this.allianceNamesValue) {
        try {
          allianceNames = typeof this.allianceNamesValue === 'string' 
            ? JSON.parse(this.allianceNamesValue) 
            : this.allianceNamesValue;
        } catch (e) {
          console.error('Error parsing allianceNames:', e);
          allianceNames = [];
        }
      }
      
      return {
        title: (context) => {
          // Para el gráfico de alianzas, usar nombres completos
          if (Array.isArray(allianceNames) && allianceNames.length > 0) {
            const index = context[0].dataIndex;
            return allianceNames[index] || context[0].label;
          }
          return context[0].label;
        },
        label: (context) => {
          if (this.indexAxisValue === 'y') {
            return 'Cupones: ' + context.parsed.x.toLocaleString();
          }
          // Para el gráfico de alianzas por valor
          if (Array.isArray(allianceNames) && allianceNames.length > 0) {
            const value = context.parsed.y;
            return 'Valor: ₲ ' + value.toLocaleString('es-PY');
          }
          return 'Cantidad: ' + context.parsed.y.toLocaleString();
        }
      };
    }
    
    return {
      label: (context) => {
        return 'Cantidad: ' + context.parsed.y.toLocaleString();
      }
    };
  }

  getScalesConfig(type) {
    if (type === 'doughnut') {
      return undefined;
    }

    const isHorizontal = this.indexAxisValue === 'y';
    
    // Parsear allianceNames si es necesario
    let allianceNames = [];
    if (this.allianceNamesValue) {
      try {
        allianceNames = typeof this.allianceNamesValue === 'string' 
          ? JSON.parse(this.allianceNamesValue) 
          : this.allianceNamesValue;
      } catch (e) {
        console.error('Error parsing allianceNames:', e);
        allianceNames = [];
      }
    }
    const isAllianceChart = Array.isArray(allianceNames) && allianceNames.length > 0;
    
    return {
      [isHorizontal ? 'x' : 'y']: {
        beginAtZero: true,
        ticks: {
          callback: (value) => {
            // Para el gráfico de alianzas por valor, formatear en millones
            if (isAllianceChart && !isHorizontal) {
              return '₲ ' + (value / 1000000).toFixed(1) + 'M';
            }
            return value.toLocaleString();
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      [isHorizontal ? 'y' : 'x']: {
        grid: {
          display: false
        },
        ticks: isHorizontal ? {} : {
          maxRotation: type === 'bar' ? 45 : 0,
          minRotation: type === 'bar' ? 45 : 0
        }
      }
    };
  }
}
